import { LogOut, Home, Sparkles } from 'lucide-react';
import { useLocation } from 'react-router-dom';

import { useAdminNavigation } from '../navigation/useAdminNavigation';
import { useWhatsNew } from '../release-notes';
import {
  adminRoutes,
  getCategoryByPath,
  getCustomizedAccessibleCategoryIds,
} from '../navigation/adminRoutes';

import AdminCockpitNavItem from './AdminCockpitNavItem';

import { Button } from '@/components/ui/button';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';

const AdminMobileDrawer = ({ onLogout, canAccessSection }) => {
  const location = useLocation();
  const {
    mobileDrawerOpen,
    setMobileDrawerOpen,
    navigationPreferences = {},
  } = useAdminNavigation();
  const { hasUnread, openWhatsNew } = useWhatsNew();

  // Filter categories and sections based on permissions
  const visibleCategories = getCustomizedAccessibleCategoryIds(
    canAccessSection,
    navigationPreferences,
    { includeHidden: true },
  );

  const handleItemClick = () => {
    setMobileDrawerOpen(false);
  };
  const activeCategoryId = getCategoryByPath(location.pathname);

  return (
    <Sheet open={mobileDrawerOpen} onOpenChange={setMobileDrawerOpen}>
      <SheetContent
        side="bottom"
        className="admin-mobile-more-sheet flex max-h-[75dvh] flex-col rounded-t-2xl border-x-0 border-b-0 border-t-2 border-amber-600 p-0"
      >
        <SheetHeader className="border-b border-stone-300 px-4 py-4 text-left dark:border-amber-600/30">
          <SheetTitle className="text-base font-bold text-stone-800 dark:text-white">
            More cockpits
          </SheetTitle>
          <SheetDescription className="text-xs text-stone-500 dark:text-stone-400">
            Open any admin dashboard
          </SheetDescription>
        </SheetHeader>

        {/* Lorenz-style More sheet; subsection links live on each cockpit dashboard. */}
        <nav
          className="grid flex-1 grid-cols-3 gap-2 overflow-y-auto p-3"
          aria-label="All cockpits"
        >
          {visibleCategories.map((categoryId) => {
            const category = adminRoutes[categoryId];

            return (
              <div
                key={categoryId}
                className="overflow-hidden rounded-xl border border-stone-200 bg-white/40 dark:border-stone-700 dark:bg-black/10"
              >
                <AdminCockpitNavItem
                  categoryId={categoryId}
                  category={category}
                  canAccessSection={canAccessSection}
                  isActive={activeCategoryId === categoryId}
                  onClick={handleItemClick}
                  compact
                />
              </div>
            );
          })}
        </nav>

        <div className="grid grid-cols-3 gap-2 border-t border-stone-300 p-3 dark:border-amber-600/30">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              const moreTrigger = document.querySelector('button[aria-label="More cockpits"]');
              setMobileDrawerOpen(false);
              openWhatsNew(moreTrigger);
            }}
            className="relative text-stone-600 hover:text-stone-900 dark:text-stone-300 dark:hover:text-white"
            aria-label={`What's New${hasUnread ? ', unread updates' : ''}`}
          >
            <span className="relative mr-2">
              <Sparkles className="h-4 w-4" aria-hidden="true" />
              {hasUnread && <span className="absolute -right-1 -top-1 h-2 w-2 rounded-full bg-amber-500" aria-hidden="true" />}
            </span>
            What&apos;s New
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => window.open('https://owazamartialarts.com', '_blank')}
            className="flex-1 text-stone-600 hover:text-stone-900 dark:text-stone-300 dark:hover:text-white"
          >
            <Home className="h-4 w-4 mr-2" />
            View Site
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setMobileDrawerOpen(false);
              onLogout();
            }}
            className="flex-1 text-stone-600 hover:text-stone-900 dark:text-stone-300 dark:hover:text-white"
          >
            <LogOut className="h-4 w-4 mr-2" />
            Logout
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default AdminMobileDrawer;
